import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import ReactDOMServer from "react-dom/server";
import { Modal } from "react-bootstrap";
import { getAlert } from "../../Services/appAlerts";
import { formatTotalHours, getAllMonths, getAllYears, getFieldData, getMonthLastDay, getTotalHours } from "../../Services/appService";
import { getCurrentUser } from "../../app/store/auth";
import {
  useLoadPartnerCandidateAppsQuery,
  useLoadCandidateAppRuningTasksQuery,
  useLazyLoadPartnerAllCandidateRptQuery,
  usePartnerCandidateGenerateBulkInvMutation
} from "../../app/store/partnerApiSlice";
import { useGetUserDetailsQuery, useGetUserProfileLogoQuery } from "../../app/store/authApiSlice";
import { usePrintGeneratedInvoiceMutation, useLazyLoadBulkAllRatesQuery } from "../../app/store/timeLogApiSlice";

const BULK_INVOICE_STYLES = {
  modalContent: {
    borderRadius: "24px",
    overflow: "hidden",
    border: "1px solid rgba(226, 232, 240, 1)",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.16)"
  },
  modalDialog: {
    maxWidth: "1120px"
  },
  modalHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    background: "linear-gradient(135deg, rgba(248, 250, 252, 0.98) 0%, rgba(239, 246, 255, 0.95) 100%)"
  },
  headerContent: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "16px"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "14px"
  },
  headerIcon: {
    width: "46px",
    height: "46px",
    borderRadius: "15px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "21px",
    boxShadow: "none"
  },
  eyebrow: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#4f46e5",
    marginBottom: "4px"
  },
  title: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#0f172a",
    lineHeight: 1.1,
    margin: 0
  },
  subtitle: {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "4px"
  },
  closeButton: {
    width: "38px",
    height: "38px",
    borderRadius: "10px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    color: "#64748b",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "16px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0
  },
  modalBody: {
    padding: "20px 22px",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)"
  },
  helperCard: {
    borderRadius: "16px",
    padding: "16px",
    background: "linear-gradient(135deg, rgba(248,250,252,0.98) 0%, rgba(239,246,255,0.9) 100%)",
    border: "1px solid rgba(191, 219, 254, 0.9)",
    marginBottom: "16px"
  },
  helperTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "8px"
  },
  helperText: {
    fontSize: "12px",
    lineHeight: 1.7,
    color: "#64748b",
    margin: 0
  },
  sectionCard: {
    borderRadius: "20px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
    padding: "18px"
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "14px",
    flexWrap: "wrap",
    marginBottom: "14px"
  },
  sectionTitle: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#0f172a",
    marginBottom: "4px"
  },
  sectionSubtitle: {
    fontSize: "12px",
    color: "#64748b",
    lineHeight: 1.6
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "14px",
    marginBottom: "16px"
  },
  fieldLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: "8px"
  },
  inputShell: {
    borderRadius: "14px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    boxShadow: "inset 0 1px 2px rgba(15, 23, 42, 0.03)"
  },
  uploadBox: {
    borderRadius: "16px",
    border: "1px dashed rgba(148, 163, 184, 1)",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    padding: "16px",
    marginTop: "16px"
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap"
  },
  cameraButton: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    border: "1px solid rgba(191, 219, 254, 1)",
    background: "#eff6ff",
    color: "#1d4ed8",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    margin: 0,
    transition: "all 0.2s ease"
  },
  tableWrap: {
    border: "1px solid rgba(226, 232, 240, 1)",
    borderRadius: "16px",
    overflow: "auto",
    background: "#ffffff"
  },
  table: {
    marginBottom: 0
  },
  tableHeadCell: {
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    borderBottom: "1px solid rgba(226, 232, 240, 0.9)",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#64748b",
    background: "#f8fafc",
    padding: "13px 14px",
    whiteSpace: "nowrap"
  },
  tableCell: {
    verticalAlign: "middle",
    padding: "13px 14px",
    borderTop: "1px solid rgba(226, 232, 240, 0.75)",
    fontSize: "13px",
    color: "#334155",
    fontWeight: 600
  },
  emptyState: {
    padding: "20px",
    textAlign: "center",
    color: "#64748b",
    fontSize: "13px"
  },
  countPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid rgba(191, 219, 254, 1)",
    background: "#eff6ff",
    color: "#1d4ed8",
    padding: "8px 12px",
    fontSize: "12px",
    fontWeight: 600
  },
  previewShell: {
    maxHeight: "700px",
    overflow: "auto",
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 1)",
    background: "#ffffff",
    padding: "18px"
  },
  printBody: {
    width: "100%",
    padding: "18px 22px",
    backgroundColor: "#ffffff",
    color: "#111827",
    fontFamily: "Arial, Helvetica, sans-serif"
  },
  printHeaderTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    marginBottom: "16px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff"
  },
  printHeaderLogoCell: {
    width: "12%",
    border: "1px solid #cbd5e1",
    padding: "10px",
    verticalAlign: "middle",
    backgroundColor: "#f8fafc"
  },
  printHeaderTitleCell: {
    width: "42%",
    padding: "14px",
    textAlign: "right",
    verticalAlign: "middle"
  },
  printLogo: {
    display: "block",
    width: "150px",
    maxWidth: "150px",
    height: "auto",
    maxHeight: "50px",
    objectFit: "contain",
    border: "1px solid #e2e8f0"
  },
  printTitleText: {
    fontSize: "32px",
    fontWeight: "bold",
    letterSpacing: "0.06em",
    padding: 0,
    color: "#0f172a",
    margin: 0
  },
  invoiceSubRef: {
    fontSize: "20px",
    color: "#2563eb",
    fontWeight: "bold",
    paddingTop: "10px",
    margin: 0
  },
  printHeaderSubText: {
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "bold",
    margin: "6px 0 0",
    padding: 0,
    textTransform: "uppercase"
  },
  printMetaTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    marginBottom: "18px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff"
  },
  printMetaCell: {
    border: "1px solid #cbd5e1",
    padding: "5px 12px",
    verticalAlign: "top",
    backgroundColor: "#f8fafc"
  },
  printMetaCellAlt: {
    border: "1px solid #bfdbfe",
    padding: "5px 12px",
    verticalAlign: "top",
    textAlign: "right",
    backgroundColor: "#eff6ff"
  },
  printMetaLabel: {
    color: "#475569",
    fontSize: "10px",
    fontWeight: "bold",
    letterSpacing: "0.06em",
    margin: "0 0 5px",
    padding: 0,
    textTransform: "uppercase"
  },
  printMetaValue: {
    whiteSpace: "nowrap",
    color: "#111827",
    fontSize: "16px",
    fontWeight: "bold",
    lineHeight: 1.15,
    margin: "0 0 5px",
    padding: 0
  },
  printPartyTable: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
    marginBottom: "14px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff"
  },
  printPanelTitle: {
    fontSize: "10px",
    fontWeight: "bold",
    color: "#475569",
    padding: "8px 10px",
    backgroundColor: "#f8fafc",
    border: "1px solid #cbd5e1",
    letterSpacing: "0.06em",
    textTransform: "uppercase"
  },
  printPanelTitleAlt: {
    fontSize: "10px",
    fontWeight: "bold",
    color: "#1d4ed8",
    padding: "8px 10px",
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
    letterSpacing: "0.06em",
    textTransform: "uppercase"
  },
  printPanelCell: {
    border: "1px solid #cbd5e1",
    borderTop: "0",
    padding: "12px 10px",
    fontSize: "12px",
    lineHeight: 1.45,
    color: "#334155",
    verticalAlign: "top"
  },
  printPanelCellAlt: {
    border: "1px solid #bfdbfe",
    borderTop: "0",
    padding: "12px 10px",
    fontSize: "12px",
    lineHeight: 1.45,
    color: "#334155",
    verticalAlign: "top",
    backgroundColor: "#f8fbff"
  },
  printAddressMeta: {
    margin: "0 0 6px",
    padding: 0,
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "bold",
    letterSpacing: "0.05em",
    textTransform: "uppercase"
  },
  printAddressName: {
    margin: "0 0 6px",
    padding: 0,
    color: "#111827",
    fontSize: "14px",
    fontWeight: "bold"
  },
  printAddressLine: {
    margin: "0 0 5px",
    padding: 0,
    color: "#475569",
    fontSize: "11px"
  },
  printDetailTable: {
    width: "100%",
    borderCollapse: "collapse",
    margin: "8px 0 16px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#ffffff",
    tableLayout: "fixed"
  },
  printDetailTitle: {
    padding: "7px 9px",
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#334155",
    fontSize: "10px",
    fontWeight: "bold",
    letterSpacing: "0.05em",
    textTransform: "uppercase"
  },
  printDetailLabel: {
    width: "17%",
    padding: "7px 8px",
    border: "1px solid #e2e8f0",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    backgroundColor: "#f8fafc"
  },
  printDetailValue: {
    width: "33%",
    padding: "7px 8px",
    border: "1px solid #e2e8f0",
    color: "#111827",
    fontSize: "10px",
    fontWeight: "bold",
    verticalAlign: "middle",
    wordBreak: "break-word"
  },
  printTable: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #cbd5e1",
    margin: "0 0 16px",
    backgroundColor: "#ffffff",
    tableLayout: "fixed"
  },
  printTableTitleCell: {
    padding: "9px 10px",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    fontSize: "12px",
    fontWeight: "bold",
    letterSpacing: "0.04em",
    textTransform: "uppercase"
  },
  printTableHeadCell: {
    backgroundColor: "#334155",
    border: "1px solid #334155",
    color: "#ffffff",
    fontSize: "9px",
    fontWeight: "bold",
    letterSpacing: "0.04em",
    padding: "8px 7px",
    textAlign: "left",
    textTransform: "uppercase",
    verticalAlign: "middle"
  },
  printTableHeadCellRight: {
    backgroundColor: "#334155",
    border: "1px solid #334155",
    color: "#ffffff",
    fontSize: "9px",
    fontWeight: "bold",
    letterSpacing: "0.04em",
    padding: "8px 7px",
    textAlign: "right",
    textTransform: "uppercase",
    verticalAlign: "middle"
  },
  printTableCell: {
    border: "1px solid #e2e8f0",
    color: "#111827",
    fontSize: "10px",
    lineHeight: 1.35,
    padding: "9px 7px",
    verticalAlign: "top",
    wordBreak: "break-word"
  },
  printTableCellRight: {
    border: "1px solid #e2e8f0",
    color: "#111827",
    fontSize: "10px",
    lineHeight: 1.35,
    padding: "9px 7px",
    textAlign: "right",
    verticalAlign: "top",
    whiteSpace: "nowrap"
  },
  printTableFinalCell: {
    border: "1px solid #bfdbfe",
    backgroundColor: "#eff6ff",
    color: "#0f172a",
    fontSize: "11px",
    fontWeight: "bold",
    lineHeight: 1.35,
    padding: "9px 7px",
    textAlign: "right",
    verticalAlign: "top",
    whiteSpace: "nowrap"
  },
  printSummarySpacerCell: {
    border: "0",
    backgroundColor: "#ffffff",
    padding: 0
  },
  printSummaryGapCell: {
    border: "0",
    backgroundColor: "#ffffff",
    fontSize: "1px",
    height: "20px",
    lineHeight: "20px",
    padding: 0
  },
  printSummaryMiniGapCell: {
    border: "0",
    backgroundColor: "#ffffff",
    fontSize: "1px",
    height: "5px",
    lineHeight: "5px",
    padding: 0
  },
  printTotalLabel: {
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontSize: "11px",
    fontWeight: "bold",
    padding: "10px 9px",
    textAlign: "right",
    border: "1px solid #e2e8f0"
  },
  printTotalValue: {
    backgroundColor: "#f8fafc",
    color: "#111827",
    fontSize: "11px",
    fontWeight: "bold",
    padding: "10px 9px",
    textAlign: "right",
    border: "1px solid #e2e8f0"
  },
  printGrandTotalLabel: {
    backgroundColor: "#0f172a",
    border: "1px solid #0f172a",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "12px 9px",
    textAlign: "right"
  },
  printGrandTotalValue: {
    backgroundColor: "#0f172a",
    border: "1px solid #0f172a",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "12px 9px",
    textAlign: "right"
  },
  printServiceBox: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    marginBottom: "18px",
    marginTop: "38px",
    paddingTop: "30px",
    tableLayout: "fixed"
  },
  printServiceTitleCell: {
    width: "20%",
    border: "1px solid #cbd5e1",
    backgroundColor: "#f8fafc",
    color: "#475569",
    fontSize: "10px",
    fontWeight: "bold",
    letterSpacing: "0.06em",
    padding: "10px 12px",
    textTransform: "uppercase",
    verticalAlign: "top"
  },
  printServiceTextCell: {
    border: "1px solid #cbd5e1",
    backgroundColor: "#ffffff",
    color: "#334155",
    fontSize: "11px",
    lineHeight: 1.5,
    padding: "10px 12px",
    verticalAlign: "top"
  },
  modalFooter: {
    padding: "16px 22px 20px",
    borderTop: "1px solid rgba(226, 232, 240, 0.9)",
    background: "#ffffff"
  },
  footerActions: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    flexWrap: "wrap"
  },
  subtleButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    borderRadius: "8px",
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(37, 99, 235, 0.2)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  }
};

const EMPTY_ARRAY = [];

const BulkInvoice = (props) => {

  const { bulkShowHide, handleBulkShowHide } = props;

  const currentUser = useMemo(() => getCurrentUser(), []);
  const usrId = currentUser?.linkUser || currentUser?.id;

  // RTK Query hooks
  const { data: userDetailsSelector = null } = useGetUserDetailsQuery(usrId, { skip: !usrId });
  const { data: userProfilePicSelector } = useGetUserProfileLogoQuery(usrId, { skip: !usrId });
  const { data: candidateAppsSelector = [] } = useLoadPartnerCandidateAppsQuery(usrId, { skip: !usrId });

  const currentWorkflow = useMemo(() => {
    const mainWorkflow = candidateAppsSelector.filter(x => x.workflowType === 1);
    return mainWorkflow[0] || null;
  }, [candidateAppsSelector]);

  const { data: appTasksSelector = [] } = useLoadCandidateAppRuningTasksQuery(
    { wfId: currentWorkflow?.oldWorkflowId, userId: usrId },
    { skip: !currentWorkflow?.oldWorkflowId || !usrId }
  );

  const [triggerLoadCandidates, { data: partnerCandidatesReportSelector = EMPTY_ARRAY }] = useLazyLoadPartnerAllCandidateRptQuery();
  const [triggerLoadAllRates, { data: bulkAllRatesData = EMPTY_ARRAY }] = useLazyLoadBulkAllRatesQuery();
  const [generateBulkInv] = usePartnerCandidateGenerateBulkInvMutation();
  const [printInvoice] = usePrintGeneratedInvoiceMutation();

  const [candidateObj, setCandidateObj] = useState(null);
  const [candidateList, setCandidateList] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [isGenerated, setIsGenerated] = useState(false);
  const [pdfBtnProccess, setPdfBtnProccess] = useState(false);
  const [saveBtnProccess, setSaveBtnProccess] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [logoPicObj, setLogoPicObj] = useState(null);

  const months = useMemo(() => getAllMonths(), []);
  const years = useMemo(() => getAllYears(), []);

  const printBodyRef = useRef();
  const printFooterRef = useRef();

  const partType = useMemo(() => {
    if (userDetailsSelector?.partnerType === "Sub-Contractors") {
      return "subcontractor";
    }
    return "recruiter";
  }, [userDetailsSelector]);

  const ddName = useMemo(() => {
    if (userDetailsSelector?.partnerType === "Sub-Contractors") {
      return "subcontractorenrollmentform";
    }
    return "recruiterenrollmentform";
  }, [userDetailsSelector]);

  const partnerId = userDetailsSelector?.partnerid || 0;
  const userDetails = userDetailsSelector;
  const logoData = userProfilePicSelector || null;

  const imagePreview = useMemo(() => {
    if (logoPicObj) {
      return (
        <img
          src={logoPicObj.imagePreviewUrl}
          alt="Company Logo"
          className="img-thumbnail profilePrev"
        />
      );
    }
    return (
      <img
        alt="Company Logo"
        className="img-thumbnail profilePrev2"
        src={`${!logoData ? "../../assets/img/theme/logo.png" : process.env.REACT_APP_API_BASEURL + "/CompanyLogo/" + logoData}`}
      />
    );
  }, [logoPicObj, logoData]);

  const allRates = bulkAllRatesData;

  const wfTask = useMemo(() => {
    if (appTasksSelector.length === 0) return null;
    const filterTasks = appTasksSelector.filter(x => x.taskType === 1 && x.wiid > 0);
    if (filterTasks.length > 0) {
      const reviewTasks = filterTasks.filter(x => x.registrationType === "WP");
      if (reviewTasks.length > 0) {
        return reviewTasks[reviewTasks.length - 1];
      }
      return filterTasks[0];
    }
    return null;
  }, [appTasksSelector]);

  useEffect(() => {
    if (!bulkShowHide) {
      setSelectedYear("");
      setSelectedMonth("");
      setCandidateList([]);
    }
  }, [bulkShowHide]);

  useEffect(() => {
    if (partnerCandidatesReportSelector && partnerCandidatesReportSelector.length > 0) {
      setCandidateList(partnerCandidatesReportSelector);
      setCandidateObj(partnerCandidatesReportSelector[0]);
      const canIds = partnerCandidatesReportSelector.map((x) => x.id);
      triggerLoadAllRates(canIds);
    } else {
      setCandidateList((prev) => (prev.length > 0 ? EMPTY_ARRAY : prev));
      setCandidateObj((prev) => (prev !== null ? null : prev));
    }
  }, [partnerCandidatesReportSelector, triggerLoadAllRates]);

  const formatDateMMDDYYYY = useCallback((date = new Date()) => {
    const parsedDate = date instanceof Date ? date : new Date(date);
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    const year = parsedDate.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  const formatDecimal2 = useCallback((value) => (Number(value) || 0).toFixed(2), []);

  const getMonthlyPeriod = useCallback((obj) => {
    if (!obj?.workingHrs) return "";

    let wt;
    try {
      wt = JSON.parse(obj.workingHrs);
    } catch (e) {
      return "";
    }

    if (!Array.isArray(wt) || wt.length === 0) return "";

    const sortWt = [...wt].sort(
      (a, b) => new Date(a.start) - new Date(b.start)
    );

    const first = sortWt[0];
    const last = sortWt[sortWt.length - 1];

    const lastDate =
      last.type === "m"
        ? getMonthLastDay(last.start)
        : last.start;

    return (
      <p>
        {formatDateMMDDYYYY(first.start)} -{" "}
        {formatDateMMDDYYYY(lastDate)}
      </p>
    );
  }, [formatDateMMDDYYYY]);

  const getAdjustmentAmout = useCallback((itemObj) => {
    const adjMinus = parseFloat(itemObj?.timeLogInvoice?.adjustmentMinues || 0);
    const adjPlus = parseFloat(itemObj?.timeLogInvoice?.adjustmentPlus || 0);
    const adpDeduction = parseFloat(itemObj?.timeLogInvoice?.adpDeduction || 0);

    const adjustment = -adjMinus + adjPlus - adpDeduction;

    return <span>{adjustment.toFixed(2)}</span>;
  }, []);

  const getApplicableRateDetails = useCallback((itemObj, type) => {
    const itemYear = parseInt(itemObj?.timeLog?.year, 10);
    const itemMonth = parseInt(itemObj?.timeLog?.month, 10);
    const rateField = type === "recruiter" ? "recruiterRate" : "subcontractorRate";
    const dateField = type === "recruiter" ? "recruiterEffectiveDate" : "subcontractorEffectiveDate";

    let localRate = 0;
    if (itemObj?.rate?.recruiterRate > 0 && type === "recruiter")
      localRate = itemObj?.rate?.recruiterRate;

    if (itemObj?.rate?.subcontractorRate > 0 && type !== "recruiter")
      localRate = itemObj?.rate?.subcontractorRate;

    const matchedRate = Array.isArray(allRates)
      ? allRates.filter(x => x.performerId === itemObj.id).reduce((best, rateItem, index) => {
        if (!itemYear || !itemMonth) return best;

        const effMs = new Date(rateItem?.[dateField]).getTime();
        if (Number.isNaN(effMs)) return best;

        const effDt = new Date(effMs);
        const effYear = effDt.getFullYear();
        const effMonth = effDt.getMonth() + 1;
        const isApplicable =
          effYear < itemYear ||
          (effYear === itemYear && effMonth <= itemMonth);

        if (!isApplicable) return best;
        if (!best) return { rate: rateItem, index, effMs };

        if (effMs > best.effMs) return { rate: rateItem, index, effMs };
        if (effMs < best.effMs) return best;

        const addedMs = new Date(rateItem?.addedDateTime).getTime();
        const bestAddedMs = new Date(best.rate?.addedDateTime).getTime();
        const safeAddedMs = Number.isNaN(addedMs) ? -Infinity : addedMs;
        const safeBestAddedMs = Number.isNaN(bestAddedMs) ? -Infinity : bestAddedMs;

        if (safeAddedMs > safeBestAddedMs) return { rate: rateItem, index, effMs };
        if (safeAddedMs < safeBestAddedMs) return best;

        const idNum = Number(rateItem?.id);
        const bestIdNum = Number(best.rate?.id);
        const safeIdNum = Number.isFinite(idNum) ? idNum : -Infinity;
        const safeBestIdNum = Number.isFinite(bestIdNum) ? bestIdNum : -Infinity;

        if (safeIdNum > safeBestIdNum) return { rate: rateItem, index, effMs };
        if (safeIdNum < safeBestIdNum) return best;

        return index > best.index ? { rate: rateItem, index, effMs } : best;
      }, null)?.rate
      : null;
    const fallbackRate = parseFloat(localRate || 0);
    const rateValue =
      matchedRate?.[rateField] !== null &&
        matchedRate?.[rateField] !== undefined &&
        matchedRate?.[rateField] !== ""
        ? parseFloat(matchedRate[rateField])
        : fallbackRate;

    const effectiveDate = matchedRate?.[dateField] || localRate;

    return {
      rate: rateValue,
      effectiveDate,
    };
  }, [allRates]);

  const getCandidateRate = useCallback((itemObj, type) => {
    const { rate: applicableRate } = getApplicableRateDetails(itemObj, type);
    return applicableRate;
  }, [getApplicableRateDetails]);

  const getFinalAmout = useCallback((itemObj, type) => {
    const currentRate = getCandidateRate(itemObj, type);
    const fAmt = getTotalHours(itemObj?.timeLog?.workingHrs) * currentRate;
    return formatDecimal2(fAmt);
  }, [getCandidateRate, formatDecimal2]);

  const getFinalPay = useCallback((itemObj, type) => {
    const currentRate = getCandidateRate(itemObj, type);

    const totalHours = getTotalHours(itemObj?.timeLog?.workingHrs);
    let finalAmount = totalHours * currentRate;

    const adjMinus = parseFloat(itemObj?.timeLogInvoice?.adjustmentMinues || 0);
    const adjPlus = parseFloat(itemObj?.timeLogInvoice?.adjustmentPlus || 0);
    const adpDeduction = parseFloat(itemObj?.timeLogInvoice?.adpDeduction || 0);

    finalAmount = finalAmount - adjMinus + adjPlus - adpDeduction;

    return formatDecimal2(finalAmount);
  }, [getCandidateRate, formatDecimal2]);

  const handleInvGenerate = useCallback((type) => {
    if (type === "close") {
      setSelectedCandidates([]);
      setIsGenerated(false);
      handleBulkShowHide("");
    } else if (isGenerated) {
      setSelectedCandidates([]);
      setIsGenerated(false);
    } else {
      setIsGenerated(true);
    }
  }, [isGenerated, handleBulkShowHide]);

  const handleImageChange = useCallback((e) => {
    e.preventDefault();

    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPicObj({
        file,
        fileName: file.name,
        imagePreviewUrl: reader.result,
      });
    };

    reader.readAsDataURL(file);
  }, []);

  const doSubmit = useCallback(() => {
    if (selectedYear && selectedMonth && partnerId) {
      triggerLoadCandidates({
        year: selectedYear,
        month: selectedMonth,
        partId: partnerId,
        type: partType,
        candId: 0,
      });
    }
  }, [selectedYear, selectedMonth, partnerId, partType, triggerLoadCandidates]);

  const handleSelectCandidates = useCallback((itemObj) => {
    const obj = selectedCandidates.filter((x) => x.id === itemObj.id);
    const candList = [...selectedCandidates];
    if (obj.length > 0) {
      const filterCandidates = selectedCandidates.filter(
        (x) => x.id !== itemObj.id
      );
      setSelectedCandidates(filterCandidates);
    } else {
      candList.push(itemObj);
      setSelectedCandidates(candList);
    }
  }, [selectedCandidates]);

  const getSubTotals = useCallback((candidates, type) => {
    const total = candidates.reduce((acc, itemObj) => {
      const fAmt = parseFloat(getFinalPay(itemObj, type)) || 0;
      return acc + fAmt;
    }, 0);

    return formatDecimal2(total);
  }, [getFinalPay, formatDecimal2]);

  const handlePrintPdf = useCallback(async () => {
    setPdfBtnProccess(true);
    const htmlBody = printBodyRef.current.outerHTML;
    const frmData = new FormData();
    frmData.append("html", htmlBody);
    try {
      const blob = await printInvoice(frmData).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      getAlert("Error", "Failed to print PDF.", "error");
    } finally {
      setPdfBtnProccess(false);
    }
  }, [printInvoice]);

  const getInvoiceBody = useCallback((candidates) => {
    return (
      <tbody>
        {candidates.map((itemObj) => {
          const agencyName = getFieldData(
            itemObj?.performerData,
            "projectengagement",
            "AgencyName"
          ) || "N/A";

          const agencyState = getFieldData(
            itemObj?.performerData,
            "projectengagement",
            "State"
          ) || "N/A";
          return <tr key={itemObj.id}>
            <td style={BULK_INVOICE_STYLES.printTableCell}>
              <p style={{ fontWeight: 'bold' }}>#{itemObj.id}</p>
              <p>{itemObj.name} {itemObj.lastName}</p>
              <p>Agency : {agencyName}</p>
              <p>Location : {agencyState}</p>
            </td>
            <td style={BULK_INVOICE_STYLES.printTableCell}>{getMonthlyPeriod(itemObj?.timeLog)}</td>
            <td align="right" style={BULK_INVOICE_STYLES.printTableCellRight}>
              {formatTotalHours(itemObj?.timeLog?.workingHrs)}
            </td>
            <td align="right" style={BULK_INVOICE_STYLES.printTableCellRight}>
              {formatDecimal2(getCandidateRate(itemObj, partType))}
            </td>
            <td align="right" style={BULK_INVOICE_STYLES.printTableCellRight}>
              {getFinalAmout(itemObj, partType)}
            </td>
            <td align="right" style={BULK_INVOICE_STYLES.printTableCellRight}>
              {getAdjustmentAmout(itemObj)}
            </td>
            <td align="right" style={BULK_INVOICE_STYLES.printTableFinalCell}>
              {getFinalPay(itemObj, partType)}
            </td>
          </tr>;
        })}
      </tbody>
    );
  }, [partType, getMonthlyPeriod, getCandidateRate, getFinalAmout, getAdjustmentAmout, getFinalPay, formatDecimal2]);

  const getInvoiceHeader = useCallback((obj) => {
    const canObj = selectedCandidates.filter(x => x.id === obj.id)[0] || obj;
    return (
      <table
        width="100%"
        cellSpacing="0"
        border="0"
        style={BULK_INVOICE_STYLES.printHeaderTable}
      >
        <tbody>
          <tr>
            <td style={BULK_INVOICE_STYLES.printHeaderLogoCell}>
              {(logoPicObj?.imagePreviewUrl || logoData) && (
                <img
                  id="Img1"
                  alt="Company Logo"
                  src={logoPicObj?.imagePreviewUrl || `${process.env.REACT_APP_API_BASEURL}/CompanyLogo/${logoData}`}
                  style={BULK_INVOICE_STYLES.printLogo}
                />
              )}
            </td>
            <td style={BULK_INVOICE_STYLES.printHeaderTitleCell}>
              <p style={BULK_INVOICE_STYLES.printTitleText}>INVOICE</p>
              <p style={BULK_INVOICE_STYLES.invoiceSubRef}>#{canObj?.invoiceNumber || "Bulk"}</p>
              <p style={BULK_INVOICE_STYLES.printHeaderSubText}>Generated electronic invoice</p>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }, [logoPicObj, logoData, selectedCandidates]);

  const getInvoiceInfo = useCallback(() => {
    const companyName = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Name");
    const companyAddress = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Address_Line1");
    const companyCity = userDetails && getFieldData(userDetails.performerData, ddName, "Company_City");
    const companyState = userDetails && getFieldData(userDetails.performerData, ddName, "Company_State");
    const companyZip = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Zip_Code");
    const federalId = userDetails && getFieldData(userDetails.performerData, ddName, "Federal_ID");
    const phoneCode = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Phone_Code");
    const phone = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Phone");
    const email = userDetails && getFieldData(userDetails.performerData, ddName, "Company_Email");

    return (
      <React.Fragment>
        <table width="100%" cellSpacing="0" border="0" style={BULK_INVOICE_STYLES.printMetaTable}>
          <tbody>
            <tr>
              <td width="50%" style={BULK_INVOICE_STYLES.printMetaCell}>
                <p style={BULK_INVOICE_STYLES.printMetaLabel}>Invoice Date</p>
                <p style={BULK_INVOICE_STYLES.printMetaValue}>{formatDateMMDDYYYY()}</p>
              </td>
              <td width="50%" style={BULK_INVOICE_STYLES.printMetaCellAlt}>
                <p style={BULK_INVOICE_STYLES.printMetaLabel}>Payment Term</p>
                <p style={BULK_INVOICE_STYLES.printMetaValue}>Net 30 Days</p>
              </td>
            </tr>
          </tbody>
        </table>

        <table cellPadding="10" cellSpacing="0" width="100%" border="0" style={BULK_INVOICE_STYLES.printPartyTable}>
          <tbody>
            <tr>
              <td style={BULK_INVOICE_STYLES.printPanelTitle} width="55%">Invoice From</td>
              <td style={BULK_INVOICE_STYLES.printPanelTitleAlt} width="45%">Invoice To</td>
            </tr>
            <tr>
              <td style={BULK_INVOICE_STYLES.printPanelCell} width="55%" valign="top">
                <p style={BULK_INVOICE_STYLES.printAddressMeta}>{partType === "recruiter" ? "RECRUITER" : "SUBCONTRACTOR"}</p>
                <p style={BULK_INVOICE_STYLES.printAddressName}>{companyName || "N/A"}</p>
                <p style={BULK_INVOICE_STYLES.printAddressLine}>{companyAddress || "N/A"}</p>
                <p style={BULK_INVOICE_STYLES.printAddressLine}>
                  {[companyCity, [companyState, companyZip].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "N/A"}
                </p>
              </td>
              <td style={BULK_INVOICE_STYLES.printPanelCellAlt} width="45%" valign="top">
                <p style={BULK_INVOICE_STYLES.printAddressMeta}>CLIENT</p>
                <p style={BULK_INVOICE_STYLES.printAddressName}>IPCS Inc.</p>
                <p style={BULK_INVOICE_STYLES.printAddressLine}>600 Highway 169 South</p>
                <p style={BULK_INVOICE_STYLES.printAddressLine}>METROPOINT, Suite 1595</p>
                <p style={BULK_INVOICE_STYLES.printAddressLine}>Minneapolis, MN 55426</p>
              </td>
            </tr>
          </tbody>
        </table>

        <table cellPadding="10" cellSpacing="0" border="0" style={BULK_INVOICE_STYLES.printDetailTable}>
          <tbody>
            <tr>
              <td colSpan={4} style={BULK_INVOICE_STYLES.printDetailTitle}>
                {partType === "recruiter" ? "Recruiter" : "Subcontractor"} Details
              </td>
            </tr>
            <tr>
              <td style={BULK_INVOICE_STYLES.printDetailLabel}>ID</td>
              <td style={BULK_INVOICE_STYLES.printDetailValue}>{partnerId || "N/A"}</td>
              <td style={BULK_INVOICE_STYLES.printDetailLabel}>Phone</td>
              <td style={BULK_INVOICE_STYLES.printDetailValue}>{
                (() => {
                  let cleanCode = String(phoneCode ?? "").trim();
                  let cleanPhone = String(phone ?? "").trim();
                  if (cleanCode.includes("_")) cleanCode = cleanCode.split("_")[0].trim();
                  const digits = cleanPhone.replace(/\D/g, "");
                  let formatted = cleanPhone;
                  if (digits.length === 10) {
                    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                  } else if (digits.length === 11 && digits.startsWith("1")) {
                    if (!cleanCode) cleanCode = "+1";
                    formatted = `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
                  }
                  if (cleanCode && formatted) return `${cleanCode} ${formatted}`;
                  return cleanCode || formatted || "N/A";
                })()
              }</td>
            </tr>
            <tr>
              <td style={BULK_INVOICE_STYLES.printDetailLabel}>Federal ID</td>
              <td style={BULK_INVOICE_STYLES.printDetailValue}>{federalId || "N/A"}</td>
              <td style={BULK_INVOICE_STYLES.printDetailLabel}>Email</td>
              <td style={BULK_INVOICE_STYLES.printDetailValue}>{email || "N/A"}</td>
            </tr>
          </tbody>
        </table>
      </React.Fragment>
    );
  }, [userDetails, ddName, partType, partnerId, formatDateMMDDYYYY]);

  const getInvoiceFotter = useCallback(() => {
    return (
      <table width="100%" cellPadding="10" cellSpacing="0" border="0" style={BULK_INVOICE_STYLES.printServiceBox}>
        <tbody>
          <tr>
            <td style={BULK_INVOICE_STYLES.printServiceTitleCell}>
              Service Note
            </td>
            <td style={BULK_INVOICE_STYLES.printServiceTextCell}>
              As per IT consulting services agreement and purchase order. Thank you for your business.
            </td>
          </tr>
        </tbody>
      </table>
    );
  }, []);

  const getInvoiceMain = useCallback((candidates) => {
    return (
      <React.Fragment>
        <table
          cellPadding="10"
          cellSpacing="0"
          width="100%"
          style={BULK_INVOICE_STYLES.printTable}
          border="0"
        >
          <thead>
            <tr>
              <th colSpan={7} style={BULK_INVOICE_STYLES.printTableTitleCell}>
                Billing Summary
              </th>
            </tr>
            <tr>
              <th width="32%" style={BULK_INVOICE_STYLES.printTableHeadCell}>Candidate</th>
              <th width="12%" style={BULK_INVOICE_STYLES.printTableHeadCell}>Period</th>
              <th width="7%" style={BULK_INVOICE_STYLES.printTableHeadCellRight}>Hours</th>
              <th width="11%" style={BULK_INVOICE_STYLES.printTableHeadCellRight}>Rate/Hr ($)</th>
              <th width="11%" style={BULK_INVOICE_STYLES.printTableHeadCellRight}>Invoice ($)</th>
              <th width="13%" style={BULK_INVOICE_STYLES.printTableHeadCellRight}>Adjustment ($)</th>
              <th width="14%" style={BULK_INVOICE_STYLES.printTableHeadCellRight}>Final Amount ($)</th>
            </tr>
          </thead>
          {getInvoiceBody(candidates)}
          <tbody>
            <tr>
              <td colSpan={7} style={BULK_INVOICE_STYLES.printSummaryGapCell}>
                &nbsp;
              </td>
            </tr>
            <tr>
              <td colSpan={5} style={BULK_INVOICE_STYLES.printSummarySpacerCell}></td>
              <td align="right" style={BULK_INVOICE_STYLES.printTotalLabel}>
                SubTotal ($)
              </td>
              <td align="right" style={BULK_INVOICE_STYLES.printTotalValue}>
                {getSubTotals(candidates, partType)}
              </td>
            </tr>
            <tr>
              <td colSpan={7} style={BULK_INVOICE_STYLES.printSummaryMiniGapCell}>
                &nbsp;
              </td>
            </tr>
            <tr>
              <td colSpan={5} style={BULK_INVOICE_STYLES.printSummarySpacerCell}></td>
              <td align="right" style={BULK_INVOICE_STYLES.printGrandTotalLabel}>
                Total ($)
              </td>
              <td align="right" style={BULK_INVOICE_STYLES.printGrandTotalValue}>
                {getSubTotals(candidates, partType)}
              </td>
            </tr>
          </tbody>
        </table>
      </React.Fragment>
    );
  }, [getInvoiceBody, getSubTotals, partType]);

  const getUpdatedHtml = useCallback((newList) => {
    return ReactDOMServer.renderToStaticMarkup(
      <div style={BULK_INVOICE_STYLES.printBody}>
        {getInvoiceHeader(newList[0])}
        {getInvoiceInfo()}
        {getInvoiceMain(newList)}
        {getInvoiceFotter()}
      </div>
    );
  }, [getInvoiceHeader, getInvoiceInfo, getInvoiceMain, getInvoiceFotter]);

  const handleSaveGenerate = useCallback(async () => {
    setSaveBtnProccess(true);
    const targetUserId = currentUser?.linkUser || currentUser?.id;

    try {
      await Promise.all(
        selectedCandidates.map((itemObj) => {
          const newList = [itemObj];
          const htmlBody = getUpdatedHtml(newList);

          const frmData = new FormData();
          frmData.append("html", htmlBody);
          frmData.append("candidate", JSON.stringify(itemObj));
          frmData.append("sign", "");
          frmData.append("remarks", "Invoice");
          frmData.append("userId", targetUserId);
          frmData.append("invNum", "");
          frmData.append("workflowTask", JSON.stringify(wfTask));

          return generateBulkInv(frmData).unwrap();
        })
      );

      setSaveBtnProccess(false);
      getAlert("", "Generated and Sent Successfully", "success");
      setCandidateList([]);
      setSelectedCandidates([]);
      setIsGenerated(false);
    } catch (err) {
      setSaveBtnProccess(false);
      getAlert("Error", "Failed to generate some invoices.", "error");
    }
  }, [selectedCandidates, currentUser, wfTask, generateBulkInv, getUpdatedHtml]);

  const handleSelect = useCallback(({ currentTarget }) => {
    const id = currentTarget.dataset.obj;
    const obj = candidateList.find((x) => x.id === parseInt(id, 10));
    if (currentTarget.checked) {
      if (!obj.invoiceNumber) {
        getAlert("", "Please Enter Invoice Number", "error");
        currentTarget.checked = false;
      } else {
        handleSelectCandidates(obj);
      }
    } else {
      handleSelectCandidates(obj);
    }
  }, [candidateList, handleSelectCandidates]);

  const handleChangeInvNumber = useCallback(({ currentTarget }) => {
    const id = currentTarget.dataset.obj;
    const index = candidateList.findIndex(x => x.id === parseInt(id, 10));
    if (index !== -1) {
      const candList = [...candidateList];
      const obj = { ...candList[index], invoiceNumber: currentTarget.value };
      candList[index] = obj;
      setCandidateList(candList);
    }
  }, [candidateList]);

  return (
    <React.Fragment>
      <Modal
        show={bulkShowHide}
        size="xl"
        centered
        onHide={() => handleInvGenerate("close")}
        dialogClassName="bulk-invoice-modal-dialog"
        contentClassName="bulk-invoice-modal-content"
      >
        <style>{`
          .bulk-invoice-modal-dialog {
            max-width: ${BULK_INVOICE_STYLES.modalDialog.maxWidth};
          }
          .bulk-invoice-modal-content {
            border-radius: ${BULK_INVOICE_STYLES.modalContent.borderRadius};
            overflow: ${BULK_INVOICE_STYLES.modalContent.overflow};
            border: ${BULK_INVOICE_STYLES.modalContent.border};
            box-shadow: ${BULK_INVOICE_STYLES.modalContent.boxShadow};
          }
        `}</style>
        <Modal.Header style={BULK_INVOICE_STYLES.modalHeader}>
          <div style={BULK_INVOICE_STYLES.headerContent}>
            <Modal.Title as="div" style={BULK_INVOICE_STYLES.titleWrap}>
              <span style={BULK_INVOICE_STYLES.headerIcon}>
                <i className="fa fa-files-o" aria-hidden="true"></i>
              </span>
              <div>
                <div style={BULK_INVOICE_STYLES.eyebrow}>Bulk Invoice</div>
                <h2 style={BULK_INVOICE_STYLES.title}>Generate Bulk Invoices</h2>
                <div style={BULK_INVOICE_STYLES.subtitle}>
                  Load candidates for a period, select invoice numbers, then preview and send.
                </div>
              </div>
            </Modal.Title>
            <button
              type="button"
              style={BULK_INVOICE_STYLES.closeButton}
              onClick={() => handleInvGenerate("close")}
              aria-label="Close bulk invoice popup"
            >
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          </div>
        </Modal.Header>
        <Modal.Body style={BULK_INVOICE_STYLES.modalBody}>
          <div style={BULK_INVOICE_STYLES.helperCard}>
            <div style={BULK_INVOICE_STYLES.helperTitle}>
              {isGenerated ? "Review before sending" : "Select invoices to generate"}
            </div>
            <p style={BULK_INVOICE_STYLES.helperText}>
              {isGenerated
                ? "Confirm the generated invoice preview before saving or printing a PDF copy."
                : "Choose an invoice year and month, load eligible candidates, enter invoice numbers, and select the rows to include."}
            </p>
          </div>
          {!isGenerated && (
            <div style={BULK_INVOICE_STYLES.sectionCard}>
              <div style={BULK_INVOICE_STYLES.sectionHeader}>
                <div>
                  <div style={BULK_INVOICE_STYLES.sectionTitle}>Candidate Selection</div>
                  <div style={BULK_INVOICE_STYLES.sectionSubtitle}>
                    Load candidates for the selected invoice period.
                  </div>
                </div>
                <span style={BULK_INVOICE_STYLES.countPill}>
                  <i className="fa fa-check-square-o" aria-hidden="true"></i>
                  {selectedCandidates.length} selected
                </span>
              </div>

              <div style={BULK_INVOICE_STYLES.filterGrid}>
                <div>
                  <label htmlFor="year" style={BULK_INVOICE_STYLES.fieldLabel}>Invoice Year</label>
                  <select name="year" id="year"
                    className="form-control"
                    style={BULK_INVOICE_STYLES.inputShell}
                    value={selectedYear ? selectedYear.toString() : ""}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="">Year</option>
                    {years.map(y =>
                      <option key={y.name} value={y.name.toString()}>{y.name}</option>
                    )}
                  </select>

                </div>
                <div>
                  <label htmlFor="month" style={BULK_INVOICE_STYLES.fieldLabel}>Invoice Month</label>
                  <select name="month" id="month"
                    className="form-control"
                    style={BULK_INVOICE_STYLES.inputShell}
                    value={selectedMonth ? selectedMonth.toString() : ""}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                  >
                    <option value="">Month</option>
                    {months.map(m =>
                      <option key={m.id} value={m.id.toString()}>{m.name}</option>
                    )}
                  </select>

                </div>
                <div className="d-flex align-items-end">
                  <button
                    className="btn btn-sm"
                    style={BULK_INVOICE_STYLES.primaryButton}
                    disabled={!selectedYear || !selectedMonth}
                    onClick={doSubmit}
                  >
                    <i className="fa fa-refresh" aria-hidden="true"></i> Load Candidates
                  </button>
                </div>
              </div>
              <div style={BULK_INVOICE_STYLES.tableWrap}>
                <div className="table-responsive">
                  <table className="table align-items-center table-hover" style={BULK_INVOICE_STYLES.table}>
                    <thead>
                      <tr>
                        <th style={BULK_INVOICE_STYLES.tableHeadCell}>Select</th>
                        <th style={BULK_INVOICE_STYLES.tableHeadCell}>Invoice Number</th>
                        <th style={BULK_INVOICE_STYLES.tableHeadCell}>ID</th>
                        <th style={BULK_INVOICE_STYLES.tableHeadCell}>Name</th>
                        <th style={BULK_INVOICE_STYLES.tableHeadCell}>Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidateList.map((item) => (
                        <tr key={item.id}>
                          <td style={BULK_INVOICE_STYLES.tableCell}>
                            <input
                              type="checkbox"
                              defaultChecked={item.isSelected}
                              onChange={handleSelect}
                              data-obj={item.id}
                            />
                          </td>
                          <td style={BULK_INVOICE_STYLES.tableCell}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              style={BULK_INVOICE_STYLES.inputShell}
                              value={item.invoiceNumber}
                              onChange={handleChangeInvNumber}
                              data-obj={item.id}
                              placeholder="Invoice Number"
                            />
                          </td>
                          <td style={BULK_INVOICE_STYLES.tableCell}>{item.id}</td>
                          <td style={BULK_INVOICE_STYLES.tableCell}>
                            <span className="text-uppercase font-weight-bold">
                              {item.name} {item.lastName}
                            </span>
                          </td>
                          <td style={BULK_INVOICE_STYLES.tableCell}>{item.email}</td>
                        </tr>
                      ))}
                      {candidateList.length === 0 && (
                        <tr>
                          <td colSpan={5} style={BULK_INVOICE_STYLES.emptyState}>
                            No candidates loaded for the selected period.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div style={BULK_INVOICE_STYLES.uploadBox}>
                <label style={BULK_INVOICE_STYLES.fieldLabel}>Upload Company Logo</label>
                <div style={BULK_INVOICE_STYLES.logoRow}>
                  {imagePreview}
                  <div>
                    <label className="profileBtn" style={BULK_INVOICE_STYLES.cameraButton} title="Upload company logo">
                      <input
                        type="file"
                        onChange={handleImageChange}
                        style={{ display: "none" }}
                      />
                      <i className="fa fa-camera" aria-hidden="true"></i>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isGenerated && (
            <div style={BULK_INVOICE_STYLES.sectionCard}>
              <div style={BULK_INVOICE_STYLES.sectionHeader}>
                <div>
                  <div style={BULK_INVOICE_STYLES.sectionTitle}>Invoice Preview</div>
                  <div style={BULK_INVOICE_STYLES.sectionSubtitle}>
                    This preview includes {selectedCandidates.length} selected candidate{selectedCandidates.length === 1 ? "" : "s"}.
                  </div>
                </div>
                <span style={BULK_INVOICE_STYLES.countPill}>
                  <i className="fa fa-files-o" aria-hidden="true"></i>
                  {selectedCandidates.length} invoice{selectedCandidates.length === 1 ? "" : "s"}
                </span>
              </div>
              <div style={BULK_INVOICE_STYLES.previewShell}>
                <div id="printBody" ref={printBodyRef} style={BULK_INVOICE_STYLES.printBody}>
                  {candidateObj && getInvoiceHeader(candidateObj)}
                  {getInvoiceInfo()}
                  {/* {selectedCandidates.length > 0 && getBankDetails()} */}
                  {selectedCandidates.length > 0 &&
                    getInvoiceMain(selectedCandidates)}
                  {selectedCandidates.length > 0 && getInvoiceFotter()}
                </div>
                <div id="printFooter" ref={printFooterRef}></div>
              </div>
              {/* <div className="col-md-12 mt-3 form-group">
                  <label htmlFor="esignName" className="form-control-label">Sign Digitally</label>
                  <textarea cols="2" rows={2}
                    id="esignName"
                    name="esignName"
                    className="form-control"
                    value={esignName}
                    placeholder="Sign Digitally"
                    onChange={({ currentTarget }) => setEsignName(currentTarget.value)}
                  ></textarea>
                </div> */}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={BULK_INVOICE_STYLES.modalFooter}>
          <div style={BULK_INVOICE_STYLES.footerActions}>
            <button
              className="btn btn-sm"
              style={BULK_INVOICE_STYLES.subtleButton}
              onClick={() => handleInvGenerate("close")}
            >
              <i className="fa fa-times" aria-hidden="true"></i> Close
            </button>
            {!isGenerated && (
              <button
                className="btn btn-sm"
                style={BULK_INVOICE_STYLES.primaryButton}
                disabled={selectedCandidates.length <= 0}
                onClick={handleInvGenerate}
              >
                <i className="fa fa-cog" aria-hidden="true"></i> Generate Invoice
              </button>
            )}
            {isGenerated && (
              <>
                <button
                  className="btn btn-sm"
                  style={BULK_INVOICE_STYLES.subtleButton}
                  onClick={handleInvGenerate}
                >
                  <i className="fa fa-arrow-left" aria-hidden="true"></i> Back
                </button>
                <button
                  className="btn btn-sm"
                  style={BULK_INVOICE_STYLES.primaryButton}
                  disabled={pdfBtnProccess}
                  onClick={handlePrintPdf}
                >
                  {pdfBtnProccess ? <i className="fa fa-spinner fa-spin"></i> :
                    <i className="fa fa-print" aria-hidden="true"></i>} Print PDF
                </button>
                <button
                  className="btn btn-sm"
                  style={BULK_INVOICE_STYLES.primaryButton}
                  disabled={saveBtnProccess}
                  onClick={handleSaveGenerate}
                >
                  {saveBtnProccess ? <i className="fa fa-spinner fa-spin"></i> :
                    <i className="fa fa-check" aria-hidden="true"></i>} Generate & Save
                </button>
              </>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}

export default BulkInvoice;
